/*
 * SammyGift gift fields + delivery rules (replaces the ymq product-options app). Dependency-free.
 *
 * <sg-gift-fields>          product form (snippets/gift-fields.liquid): city select -> date -> time slot -> card note.
 *                           Fills the hidden line item properties "Delivery City" = "name_en (slug)" and
 *                           "Delivery Date & Time" = "YYYY-MM-DD HH:MM", and blocks add-to-cart until valid
 *                           (Dawn's form is `novalidate`, so a capture-phase submit guard does the validation).
 * <sg-cart-gift-edit>       cart page (snippets/sg-cart-gift-edit.liquid): edit city/date/time/note of a line via
 *                           /cart/change.js, re-validated with the same rules.
 * <sg-cart-delivery-check>  cart page (snippets/sg-cart-delivery-check.liquid): one city + one date per order notice;
 *                           disables checkout when lines go to different cities. Re-checks /cart.js after cart updates.
 *
 * Rules: each gift element contains <script type="application/json" data-sg-rules> rendered by
 * snippets/sg-delivery-rules.liquid (shape documented there; the allowed `cities` already reflect the product's
 * sammygift.delivery_scope: nationwide / mainland / tags). Algorithm = docs/delivery-rules.md section 3:
 *   lead     = max(product.lead_days, city.lead_days_override || 0)
 *   earliest = todayShanghai + lead; if lead == 0 and nowShanghai > cutoff (city.cutoff_time || settings) -> +1 day;
 *              skip blackout dates; if earliest is today and no slot is left after now -> tomorrow (skip blackouts)
 *   slots    = slot_start..slot_end inclusive every slot_step_minutes; on today only slots after now.
 * city_required == false (nationwide / mainland / no city tag): no city select is rendered, the city is not validated
 * and no "Delivery City" property is written; the cart check compares only lines that carry a Delivery City.
 * "Now" is always taken from settings.timezone (Asia/Shanghai), never the browser's timezone.
 */
(() => {
  const PROP_CITY = 'Delivery City';
  const PROP_DATETIME = 'Delivery Date & Time';
  const PROP_NOTE = 'Card Note (Optional)';
  const NOTE_MAX = 300;
  const DEFAULT_SETTINGS = {
    timezone: 'Asia/Shanghai',
    same_day_cutoff: '21:00',
    slot_start: '10:00',
    slot_end: '21:00',
    slot_step_minutes: 30,
    blackout_dates: [],
  };

  /* ---------- pure helpers ---------- */

  const toMin = (hm) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(hm || '');
    return m ? +m[1] * 60 + +m[2] : NaN;
  };
  const fromMin = (n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
  const label12 = (hm) => {
    const n = toMin(hm);
    const h = Math.floor(n / 60);
    return `${((h + 11) % 12) + 1}:${String(n % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  };
  const addDays = (ymd, days) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
  };
  const formatDate = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
      timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  };
  /** City chosen in the city picker (assets/sg-city-mode.js): localStorage "sg_city" JSON, else cookie slug. */
  function storedCity() {
    let c = null;
    try {
      c = JSON.parse(window.localStorage.getItem('sg_city') || 'null');
    } catch (e) {
      c = null;
    }
    const m = document.cookie.match(/(?:^|; )sg_city=([^;]*)/);
    const slug = m ? decodeURIComponent(m[1]) : '';
    if (c && c.s && (!slug || slug === c.s)) return c;
    return slug ? { s: slug, en: slug } : null;
  }
  const slugFromCityProp = (v) => (/\(([a-z0-9-]+)\)\s*$/.exec(v || '') || [])[1] || '';

  /** Current date (YYYY-MM-DD) and time (HH:MM) in `timeZone`. */
  function nowIn(timeZone) {
    const p = new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .reduce((o, x) => ((o[x.type] = x.value), o), {});
    return { date: `${p.year}-${p.month}-${p.day}`, hm: `${p.hour === '24' ? '00' : p.hour}:${p.minute}` };
  }

  function readRules(scope) {
    const el = scope.querySelector('script[data-sg-rules]');
    let r = {};
    try {
      r = JSON.parse(el?.textContent || '{}') || {};
    } catch (e) {
      r = {};
    }
    const settings = { ...DEFAULT_SETTINGS, ...(r.settings || {}) };
    settings.slot_step_minutes = parseInt(settings.slot_step_minutes, 10) || 30;
    if (!Array.isArray(settings.blackout_dates)) settings.blackout_dates = [];
    return {
      settings,
      product: r.product || {},
      deliverable: r.deliverable !== false && Array.isArray(r.cities) && r.cities.length > 0,
      nationwide: !!r.nationwide,
      // SammyGift: false for nationwide/mainland products -> no city select, no "Delivery City" property
      cityRequired: r.city_required !== false,
      cities: Array.isArray(r.cities) ? r.cities : [],
      loaded: !!el,
    };
  }

  /** The rules engine for one product (docs/delivery-rules.md 3.2-3.4). */
  class DeliveryRules {
    constructor(rules) {
      Object.assign(this, rules);
      this.bySlug = new Map(this.cities.map((c) => [c.slug, c]));
    }

    city(slug) {
      return this.bySlug.get(slug) || null;
    }

    now() {
      return nowIn(this.settings.timezone || 'Asia/Shanghai');
    }

    lead(city) {
      const base = parseInt(this.product.lead_days, 10);
      const override = parseInt(city?.lead_days_override, 10);
      return Math.max(Number.isFinite(base) ? base : 0, Number.isFinite(override) ? override : 0);
    }

    cutoff(city) {
      return city?.cutoff_time || this.settings.same_day_cutoff || '21:00';
    }

    isBlackout(ymd) {
      return this.settings.blackout_dates.includes(ymd);
    }

    allSlots() {
      const start = toMin(this.settings.slot_start);
      const end = toMin(this.settings.slot_end);
      const step = this.settings.slot_step_minutes;
      const out = [];
      if (!Number.isFinite(start) || !Number.isFinite(end) || step <= 0) return out;
      for (let t = start; t <= end; t += step) out.push(fromMin(t));
      return out;
    }

    /** Slots for `ymd`; on today's date only the slots after the current Shanghai time. */
    slotsFor(ymd, now = this.now()) {
      const all = this.allSlots();
      if (ymd !== now.date) return all;
      const n = toMin(now.hm);
      return all.filter((s) => toMin(s) > n);
    }

    earliest(city, now = this.now()) {
      const lead = this.lead(city);
      let d = addDays(now.date, lead);
      if (lead === 0 && toMin(now.hm) > toMin(this.cutoff(city))) d = addDays(d, 1);
      const skipBlackouts = () => {
        for (let i = 0; i < 366 && this.isBlackout(d); i++) d = addDays(d, 1);
      };
      skipBlackouts();
      if (d === now.date && this.slotsFor(d, now).length === 0) {
        d = addDays(d, 1);
        skipBlackouts();
      }
      return d;
    }

    /** Returns {msg, field} with field in city|date|time, or null when valid. */
    check(slug, date, time) {
      if (!this.deliverable) return { msg: 'Sorry, this item is currently not available for delivery.', field: 'city' };
      const city = this.cityRequired ? this.city(slug) : null;
      if (this.cityRequired && !slug) return { msg: 'Please choose the delivery city.', field: 'city' };
      if (this.cityRequired && !city) return { msg: 'This item cannot be delivered to that city. Please choose another city.', field: 'city' };
      const now = this.now();
      const min = this.earliest(city, now);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return { msg: 'Please choose a delivery date.', field: 'date' };
      if (date < min) {
        return { msg: `The earliest delivery date${city ? ` to ${city.name_en}` : ''} is ${formatDate(min)}. Please choose a later date.`, field: 'date' };
      }
      if (this.isBlackout(date)) return { msg: 'Delivery is not available on that date. Please choose another date.', field: 'date' };
      if (!/^\d{2}:\d{2}$/.test(time || '')) return { msg: 'Please choose a delivery time.', field: 'time' };
      if (!this.slotsFor(date, now).includes(time)) {
        return { msg: 'That delivery time is not available. Please choose another time.', field: 'time' };
      }
      return null;
    }
  }

  /* ---------- shared element behaviour ---------- */

  class GiftFieldsBase extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.rules = new DeliveryRules(readRules(this));
      this.citySelect = this.querySelector('[data-sg-city]');
      this.cityHidden = this.querySelector('[data-sg-city-value]');
      this.dateInput = this.querySelector('[data-sg-date]');
      this.timeSelect = this.querySelector('[data-sg-time]');
      this.datetimeHidden = this.querySelector('[data-sg-datetime]');
      this.note = this.querySelector('[data-sg-note]');
      this.count = this.querySelector('[data-sg-count]');
      this.hint = this.querySelector('[data-sg-hint]');
      this.errorEl = this.querySelector('.sg-gift-fields__error');

      this.buildCities(this.initialCity());
      this.refresh(this.initialTime());
      this.citySelect?.addEventListener('change', () => this.refresh());
      this.dateInput?.addEventListener('change', () => this.refresh());
      this.timeSelect?.addEventListener('change', () => this.sync());
      this.note?.addEventListener('input', () => this.updateCount());
      this.updateCount();
      if (!this.rules.deliverable) this.showError(this.rules.check('', '', '').msg);
      this.init?.();
    }

    initialCity() {
      return '';
    }

    initialTime() {
      return '';
    }

    /** City <select>: grouped by province, sorted by province then name_en; label "name_en name_zh". */
    buildCities(selected) {
      const sel = this.citySelect;
      if (!sel) return;
      const placeholder = sel.querySelector('option[value=""]');
      sel.innerHTML = '';
      if (placeholder) sel.append(placeholder);
      const cities = [...this.rules.cities].sort(
        (a, b) => (a.province || '~').localeCompare(b.province || '~') || a.name_en.localeCompare(b.name_en)
      );
      const groups = new Map();
      for (const c of cities) {
        const key = c.province || 'Other';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
      }
      const useGroups = groups.size > 1;
      for (const [province, list] of groups) {
        let parent = sel;
        if (useGroups) {
          parent = document.createElement('optgroup');
          parent.label = province;
          sel.append(parent);
        }
        for (const c of list) {
          const o = document.createElement('option');
          o.value = c.slug;
          o.textContent = c.name_zh ? `${c.name_en} ${c.name_zh}` : c.name_en;
          parent.append(o);
        }
      }
      if (selected && this.rules.city(selected)) sel.value = selected;
      else if (this.rules.cities.length === 1) sel.value = this.rules.cities[0].slug;
      else sel.value = '';
      sel.disabled = !this.rules.deliverable;
      if (this.dateInput) this.dateInput.disabled = !this.rules.deliverable;
      if (this.timeSelect) this.timeSelect.disabled = !this.rules.deliverable;
    }

    selectedCity() {
      if (!this.rules.cityRequired) return null;
      return this.rules.city(this.citySelect?.value || '');
    }

    /** Recompute min date + slot options after a city/date change. */
    refresh(preferredTime) {
      const now = this.rules.now();
      const city = this.selectedCity();
      const min = this.rules.earliest(city, now);
      if (this.dateInput) this.dateInput.min = min;
      if (this.hint) {
        const where = city ? ` to ${city.name_en}` : '';
        this.hint.textContent = this.rules.deliverable ? `Earliest delivery${where}: ${formatDate(min)}` : '';
        this.hint.hidden = !this.rules.deliverable;
      }
      this.buildSlots(preferredTime ?? this.timeSelect?.value, now);
      this.sync();
    }

    buildSlots(keep, now) {
      const sel = this.timeSelect;
      if (!sel) return;
      const date = this.dateInput?.value || '';
      const slots = /^\d{4}-\d{2}-\d{2}$/.test(date) ? this.rules.slotsFor(date, now) : this.rules.allSlots();
      const placeholder = sel.querySelector('option[value=""]');
      sel.innerHTML = '';
      if (placeholder) sel.append(placeholder);
      for (const s of slots) {
        const o = document.createElement('option');
        o.value = s;
        o.textContent = label12(s);
        sel.append(o);
      }
      sel.value = keep && slots.includes(keep) ? keep : '';
    }

    validate() {
      return this.rules.check(this.citySelect?.value || '', this.dateInput?.value || '', this.timeSelect?.value || '');
    }

    cityValue() {
      const c = this.selectedCity();
      return c ? `${c.name_en} (${c.slug})` : '';
    }

    value() {
      const date = this.dateInput?.value || '';
      const time = this.timeSelect?.value || '';
      return date && time ? `${date} ${time}` : '';
    }

    sync() {
      const err = this.validate();
      if (this.cityHidden) this.cityHidden.value = this.cityValue();
      if (this.datetimeHidden) this.datetimeHidden.value = err ? '' : this.value();
      if (this.errorEl && !this.errorEl.hidden) this.showError(err);
    }

    updateCount() {
      if (!this.note) return;
      if (this.note.value.length > NOTE_MAX) this.note.value = this.note.value.slice(0, NOTE_MAX);
      if (this.count) this.count.textContent = String(this.note.value.length);
    }

    showError(err) {
      const msg = typeof err === 'string' ? err : err?.msg || '';
      const field = typeof err === 'string' ? '' : err?.field || '';
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
      this.citySelect?.setAttribute('aria-invalid', field === 'city' ? 'true' : 'false');
      this.dateInput?.setAttribute('aria-invalid', field === 'date' ? 'true' : 'false');
      this.timeSelect?.setAttribute('aria-invalid', field === 'time' ? 'true' : 'false');
    }

    focusField(err) {
      const el = { city: this.citySelect, date: this.dateInput, time: this.timeSelect }[err?.field];
      el?.focus();
    }
  }

  if (!customElements.get('sg-gift-fields')) {
    customElements.define(
      'sg-gift-fields',
      class SgGiftFields extends GiftFieldsBase {
        // SammyGift city mode: preselect the city chosen in the city picker when this product can go there.
        initialCity() {
          const c = window.SGCityConfig ? storedCity() : null;
          return c && this.rules.city(c.s) ? c.s : '';
        }

        /** City mode: the chosen city cannot receive this product -> notice, add-to-cart disabled. */
        cityCheck() {
          const c = window.SGCityConfig ? storedCity() : null; // city mode switched off in theme settings -> ignore
          const block = this.querySelector('[data-sg-city-block]');
          this.cityBlocked = !!(c && this.rules.deliverable && !this.rules.city(c.s));
          if (block) block.hidden = !this.cityBlocked;
          if (!this.cityBlocked) return;
          const name = c.zh ? `${c.en} ${c.zh}` : c.en || c.s;
          const typeLabel = this.rules.product.type || 'gifts';
          const text = this.querySelector('[data-sg-city-block-text]');
          if (text) text.textContent = `Sorry, this item cannot be delivered to ${name}. Change the city, or see what we can deliver there.`;
          const link = this.querySelector('[data-sg-city-block-link]');
          if (link) {
            link.textContent = `See ${typeLabel} in ${c.en || c.s}`;
            const url = window.SGCity?.typeSearchUrl?.(this.rules.product.type || '', c);
            link.href = url || c.l || link.getAttribute('href');
            // sg-city-mode.js may load after this file: build the URL on click as well
            link.addEventListener('click', () => {
              const u = window.SGCity?.typeSearchUrl?.(this.rules.product.type || '', c);
              if (u) link.href = u;
            });
          }
          this.form?.querySelectorAll('[type="submit"], button[name="add"]').forEach((b) => {
            b.disabled = true;
            b.setAttribute('aria-disabled', 'true');
          });
          if (this.citySelect) this.citySelect.value = '';
        }

        init() {
          this.form = this.closest('form');
          this.cityCheck();
          // Changing the city in the product's own select lifts the block (the visitor is choosing explicitly).
          this.citySelect?.addEventListener('change', () => {
            if (!this.cityBlocked || !this.citySelect.value) return;
            this.cityBlocked = false;
            const block = this.querySelector('[data-sg-city-block]');
            if (block) block.hidden = true;
            this.form?.querySelectorAll('[type="submit"], button[name="add"]').forEach((b) => {
              b.disabled = false;
              b.removeAttribute('aria-disabled');
            });
          });
          // Capture-phase listener on an ancestor runs before Dawn's product-form submit handler,
          // so stopping propagation here prevents the AJAX add-to-cart.
          const host = this.closest('product-form') || this.form?.parentElement;
          if (!host || !this.form) return;
          host.addEventListener('submit', (e) => this.guard(e), true);
          // Dynamic checkout ("Buy it now") bypasses the form submit; guard its clicks too.
          host.addEventListener(
            'click',
            (e) => {
              if (e.target.closest('.shopify-payment-button')) this.guard(e);
            },
            true
          );
        }

        guard(e) {
          if (this.cityBlocked) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.querySelector('[data-sg-city-block]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            return;
          }
          // Re-run the rules with a fresh "now": the page may have been open past the cut-off.
          const err = this.validate();
          this.showError(err);
          if (!err) {
            this.cityHidden && (this.cityHidden.value = this.cityValue());
            this.datetimeHidden && (this.datetimeHidden.value = this.value());
            return;
          }
          if (err.field === 'date' || err.field === 'time') this.refresh();
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.focusField(err);
        }
      }
    );
  }

  if (!customElements.get('sg-cart-gift-edit')) {
    customElements.define(
      'sg-cart-gift-edit',
      class SgCartGiftEdit extends GiftFieldsBase {
        props() {
          try {
            return JSON.parse(this.dataset.properties || '{}') || {};
          } catch (e) {
            return {};
          }
        }

        initialCity() {
          return slugFromCityProp(this.props()[PROP_CITY]);
        }

        initialTime() {
          return this.dataset.currentTime || '';
        }

        init() {
          this.saveBtn = this.querySelector('[data-sg-save]');
          this.saveBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.save();
          });
          // Existing values that no longer pass the rules (past date, city not allowed, ...) are shown right away.
          const p = this.props();
          if (p[PROP_DATETIME] || p[PROP_CITY]) {
            const err = this.validate();
            if (err) {
              this.showError(err);
              const details = this.querySelector('details');
              if (details) details.open = true;
            }
          }
        }

        async save() {
          const err = this.validate();
          this.showError(err);
          if (err) {
            this.focusField(err);
            return;
          }
          const props = this.props();
          if (this.rules.cityRequired) props[PROP_CITY] = this.cityValue();
          else delete props[PROP_CITY];
          props[PROP_DATETIME] = this.value();
          props[PROP_NOTE] = (this.note?.value || '').slice(0, NOTE_MAX);
          this.saveBtn.setAttribute('aria-disabled', 'true');
          this.saveBtn.classList.add('loading');
          try {
            const res = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                line: parseInt(this.dataset.line, 10),
                quantity: parseInt(this.dataset.quantity, 10),
                properties: props,
              }),
            });
            if (!res.ok) throw new Error(String(res.status));
            window.location.reload();
          } catch (e) {
            this.showError('Sorry, we could not update this item. Please try again.');
            this.saveBtn.removeAttribute('aria-disabled');
            this.saveBtn.classList.remove('loading');
          }
        }
      }
    );
  }

  if (!customElements.get('sg-cart-delivery-check')) {
    customElements.define(
      'sg-cart-delivery-check',
      class SgCartDeliveryCheck extends HTMLElement {
        connectedCallback() {
          if (this._init) return;
          this._init = true;
          this.notice = this.querySelector('.sg-cart-delivery-check__notice');
          this.detail = this.querySelector('[data-sg-detail]');
          this.apply(this.dataset.citiesDiffer === 'true', this.dataset.datesDiffer === 'true');
          // Dawn re-renders only parts of the cart after quantity changes/removals: re-check from /cart.js.
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.recheck());
          }
          // Last line of defence on submit (also catches a stale page).
          document.getElementById('cart')?.addEventListener(
            'submit',
            (e) => {
              if (this.blocked) {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.notice?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }
            },
            true
          );
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        static evaluate(items) {
          const cities = new Set();
          const dates = new Set();
          for (const it of items || []) {
            const p = it.properties || {};
            const slug = slugFromCityProp(p[PROP_CITY]);
            if (slug) cities.add(slug);
            const dt = String(p[PROP_DATETIME] || '').trim();
            if (dt) dates.add(dt.split(' ')[0]);
          }
          return { citiesDiffer: cities.size > 1, datesDiffer: dates.size > 1 };
        }

        async recheck() {
          try {
            const res = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
            if (!res.ok) return;
            const cart = await res.json();
            const r = SgCartDeliveryCheck.evaluate(cart.items);
            this.apply(r.citiesDiffer, r.datesDiffer, cart.item_count === 0);
          } catch (e) {
            /* keep the current state */
          }
        }

        apply(citiesDiffer, datesDiffer, empty = false) {
          this.blocked = citiesDiffer;
          if (this.notice) this.notice.hidden = !(citiesDiffer || datesDiffer);
          if (this.detail) {
            this.detail.textContent = citiesDiffer
              ? 'Your items are going to different cities. Please change them to the same city (use "Edit delivery city, date & card note" on each item) or place a separate order for each city. Checkout is disabled until then.'
              : datesDiffer
              ? 'Your items have different delivery dates. They will all be delivered together; please set the same date on each item.'
              : '';
          }
          const btn = document.getElementById('checkout');
          if (btn) btn.disabled = citiesDiffer || empty;
          document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((el) => {
            el.hidden = citiesDiffer;
          });
        }
      }
    );
  }
})();
